import React from 'react';
import Layout from '../layout/Layout';
import CtaBand from '../layout/CtaBand';
import Container from '../ui/Container';
import Section from '../ui/Section';
import Panel from '../ui/Panel';
import Button from '../ui/Button';
import Sparkle from '../ui/Sparkle';
import Eyebrow from '../ui/Eyebrow';
import Marquee from '../ui/Marquee';
import StarRating from '../ui/StarRating';
import Reveal from '../ui/Reveal';
import ShopCard from '../ui/ShopCard';
import VideoCard from '../ui/VideoCard';
import { APP } from '../layout/siteData';
import { MAKER_VIDEOS, PLAYLIST_URL } from '../../content/makerVideos';

const IMG = '/marketing/png';

const PILLARS = [
  {
    n: '01', tint: 'lilac',
    title: 'Building your shop is fast, easy and free.',
    text: 'Creating a beautiful store couldn’t be easier with our no-code drag-and-drop designer. No credit card required.',
    cta: { label: 'Start building today', href: '/pricing-and-features' },
    img: `${IMG}/62fa3788992a3b9069d1e57e_help-you-1.png`,
  },
  {
    n: '02', tint: 'sage',
    title: 'We take the hassle out of your hustle.',
    text: 'Setting up and running your online business is a breeze with our handy tools and local team ready to support you from day one.',
    cta: { label: 'Start your shop', href: APP.signUp },
    img: `${IMG}/62fa378b2a4c4e4e5e519c05_help-you-2.png`,
  },
  {
    n: '03', tint: 'peach',
    title: 'We’ve got the tools to grow your sales.',
    text: 'Sell anything. Anywhere. Reach your customers wherever they are with social selling across Google, Instagram, Facebook and WhatsApp.',
    cta: { label: 'See our tools', href: '/pricing-and-features' },
    img: `${IMG}/6230478f5a319899eeebc44b_3rd-shapes.png`,
  },
];

const STATS = [
  { value: '4.9★', label: 'Rated on Google' },
  { value: '14 days', label: 'Free trial' },
  { value: 'from 0.05%', label: 'Transaction fees' },
  { value: '100%', label: 'South African team' },
];

const REVIEWS = [
  { name: 'Tiaan R.', avatar: `${IMG}/623053ad467a27386b5adf59_image-16.png`, text: 'What an amazing platform! It is quick and easy to set up your website and the graphics are great. The customer support is world-class. You certainly won’t go wrong with Shopstar.' },
  { name: 'Graham W.', avatar: `${IMG}/62305423ca8fc23bd9766e47_image-17.png`, text: 'All my interactions with Shopstar have been extremely professional and the service has been brilliant. A reply has never been longer than 30 minutes. Truly world class.' },
  { name: 'Gustav V.', avatar: `${IMG}/6230549ab2cf9ec8db2325a7_image-19.png`, text: 'My experience with Shopstar has been a delight. Setting up the store was really straightforward and it was easy to make it look good. Orders, payments and analytics all work well.' },
  { name: 'Nonhlanha Z.', avatar: `${IMG}/623054f3a2891ead850550b8_image-18.png`, text: 'I want to thank your team for your relentless patience and the will to always be there for clients, even with the most minor of problems. Your company is amazing.' },
  { name: 'Beth B.', avatar: `${IMG}/6230550a4a32daa2262ee0ea_image-20.png`, text: 'Shopstar is super easy to use and the support team is simply great. Very responsive and quick when you need them.' },
];

const PARTNERS = [
  { src: `${IMG}/623a1a6221fe0104f19350f6_snapscan_big.png`, alt: 'SnapScan' },
  { src: `${IMG}/623a1aa6bb757055099560ae_paystack_small.png`, alt: 'Paystack' },
  { src: `${IMG}/64da226034316920374c64a5_Ozow-Logo-Colour.png`, alt: 'Ozow' },
  { src: `${IMG}/64da2045c8e3963295518705_Bob-Go-Logo-Horizontal.png`, alt: 'Bob Go' },
  { src: `${IMG}/64da221c47edd1c43b109f6a_HappyPay_Logoblack-01.png`, alt: 'HappyPay' },
  { src: `${IMG}/64da22f1babe068ee192197d_PAYFLEX-LOGO-01.png`, alt: 'Payflex' },
  { src: `${IMG}/64da22d03ee1e9346bb3a6f5_Ayoba.png`, alt: 'Ayoba' },
  { src: `${IMG}/64da223a5ffe2951302d95d4_App-icon-+-Wordmark-Horizontal.png`, alt: 'Yoco' },
];

const ArrowLink = ({ href, children }) => (
  <a href={href} className="group inline-flex items-center gap-2 font-bold text-ink hover:text-amber transition-colors">
    {children}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
  </a>
);

const Home = ({ shops = [] }) => (
  <Layout>
    {/* HERO */}
    <section className="bg-cream">
      <Container className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-20 md:py-28">
        <div className="max-w-xl">
          <Eyebrow>For makers &amp; creators</Eyebrow>
          <h1 className="mt-6 text-display font-extrabold text-ink text-balance">
            <span className="whitespace-nowrap">Dream it.</span>{' '}
            <span className="whitespace-nowrap">Build it.</span>{' '}
            <span className="relative whitespace-nowrap">Sell it.
              <span className="absolute left-0 right-0 -bottom-1 h-[0.12em] bg-amber rounded" />
            </span>
          </h1>
          <p className="mt-6 max-w-[42ch] text-lead text-body">
            A place for South African makers and creators to start, manage and grow a successful
            online store — with built-in design, payments and shipping, all in one.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button href={APP.signUp} size="lg">Start your free trial</Button>
            <Button href="/pricing-and-features" variant="secondary" size="lg" icon={false}>See pricing</Button>
          </div>
          <div className="mt-8 flex items-center gap-3 text-sm text-muted">
            <StarRating />
            <span>Rated <strong className="text-ink font-bold">4.9</strong> by makers just like you · 14-day free trial</span>
          </div>
        </div>

        <div className="relative">
          <img
            src={`${IMG}/62fd102781171e7290af159e_sell-everythwer-iage.png`}
            alt="Sell everywhere with Shopstar — a maker selling on Google, Facebook and Instagram"
            loading="eager"
            className="relative w-full max-w-[560px] mx-auto block"
          />
        </div>
      </Container>
    </section>

    <Marquee items={['Dream it. Build it. Sell it.', 'No hidden costs', '100% South African', 'Sell everywhere', 'No code required']} />

    {/* PARTNERS */}
    <Section tone="white" pad="md">
      <p className="text-center text-xs font-extrabold uppercase tracking-[0.14em] text-muted mb-9">Payments &amp; shipping, built in</p>
      <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
        {PARTNERS.map((p) => (
          <img key={p.alt} src={p.src} alt={p.alt} loading="lazy" className="h-8 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition" />
        ))}
      </div>
    </Section>

    {/* PILLARS — alternating tinted panels */}
    <Section tone="white" pad="sm">
      <div className="max-w-[40ch] mb-12 md:mb-16">
        <Eyebrow>Every step of the way</Eyebrow>
        <h2 className="mt-4 text-h2 font-extrabold text-ink">We help you every step of the way.</h2>
        <p className="mt-3 text-lead text-body">We have the tools and the team to make your business a success.</p>
      </div>
      <div className="flex flex-col gap-6 md:gap-8">
        {PILLARS.map((p, i) => (
          <Reveal key={p.n}>
            <Panel tint={p.tint} className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className={i % 2 === 1 ? 'md:order-2' : ''}>
                <span className="inline-grid place-items-center w-11 h-11 rounded-xl bg-ink text-amber font-extrabold mb-5">{p.n}</span>
                <h3 className="text-h3 font-extrabold text-ink">{p.title}</h3>
                <p className="mt-3 text-body">{p.text}</p>
                <div className="mt-6"><ArrowLink href={p.cta.href}>{p.cta.label}</ArrowLink></div>
              </div>
              <div className={i % 2 === 1 ? 'md:order-1' : ''}>
                <img src={p.img} alt="" loading="lazy" className="w-full rounded-3xl shadow-soft" />
              </div>
            </Panel>
          </Reveal>
        ))}
      </div>
    </Section>

    {/* BENTO — all you need */}
    <Section tone="cream" pad="lg">
      <div className="max-w-[42ch] mb-12 md:mb-14">
        <Eyebrow>All in one</Eyebrow>
        <h2 className="mt-4 text-h2 font-extrabold text-ink">All you need to build, manage and grow.</h2>
        <p className="mt-3 text-lead text-body">We’ve got the tools, the team, and the determination to make your business a success.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
        <div className="lg:row-span-2 bg-white rounded-3xl p-8 border border-line flex flex-col">
          <Sparkle size={26} color="#95a8e6" />
          <h3 className="mt-5 text-h3 font-extrabold text-ink">Build.</h3>
          <p className="mt-2 text-body flex-1">A no-code, drag-and-drop designer and beautiful ready-made themes. Your dream store, live in minutes.</p>
          <img src={`${IMG}/62fa378b2a4c4e4e5e519c05_help-you-2.png`} alt="" loading="lazy" className="mt-6 rounded-2xl shadow-soft" />
        </div>
        <div className="bg-sky rounded-3xl p-8">
          <Sparkle size={24} color="#95a8e6" />
          <h3 className="mt-4 text-h3 font-extrabold text-ink">Manage.</h3>
          <p className="mt-2 text-body">Orders, payments, stock and analytics — all in one dashboard, built for the way you actually work.</p>
        </div>
        <div className="bg-cream rounded-3xl p-8">
          <Sparkle size={24} color="#ffb442" />
          <h3 className="mt-4 text-h3 font-extrabold text-ink">Sell everywhere.</h3>
          <p className="mt-2 text-body">Sell on Google, Instagram, Facebook and WhatsApp — and reach customers wherever they are.</p>
        </div>
        <div className="sm:col-span-2 bg-sky rounded-3xl p-8 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h3 className="text-h3 font-extrabold text-ink">Local support, from day one.</h3>
            <p className="mt-2 text-body max-w-[42ch]">A 100% South African Customer Success Team by email, WhatsApp and live chat.</p>
          </div>
          <Button href="/contact" variant="secondary" icon={false}>Talk to us</Button>
        </div>
      </div>
    </Section>

    {/* SUPPORT panel */}
    <Section tone="white" pad="lg">
      <Panel tint="blush" className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
        <div className="relative md:order-2">
          <img src={`${IMG}/62fa40a1fd7b990a4059ac96_bets-way-photo.png`} alt="The Shopstar Customer Success Team" loading="lazy" className="w-full rounded-3xl shadow-card" />
          <span className="absolute bottom-5 left-5 inline-flex items-center gap-2 bg-white px-4 py-2.5 rounded-full shadow-card text-[0.86rem] font-bold text-ink">
            <Sparkle size={16} color="#95a8e6" /> Hannah · Customer Success
          </span>
        </div>
        <div className="md:order-1">
          <Eyebrow>Personal support</Eyebrow>
          <h2 className="mt-4 text-h2 font-extrabold text-ink">Personal support from day one.</h2>
          <p className="mt-4 text-lead text-body max-w-[44ch]">Real people, right here in South Africa — with you from your first click to your hundredth sale, and every step in between.</p>
          <div className="mt-7"><Button href="/pricing-and-features">Start your free trial</Button></div>
        </div>
      </Panel>
    </Section>

    {/* REVIEWS */}
    <Section tone="cream" pad="lg">
      <div className="max-w-[44ch] mb-12 md:mb-14">
        <Eyebrow>Loved locally</Eyebrow>
        <h2 className="mt-4 text-h2 font-extrabold text-ink">Real 5-star reviews from makers just like you.</h2>
        <p className="mt-3 text-lead text-body">We’re rated 4.9 stars by reviewers on Google.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={(i % 3) * 70} className="bg-white rounded-3xl p-7 border border-line flex flex-col">
            <StarRating />
            <p className="mt-4 mb-6 flex-1 text-ink/85 leading-relaxed">{r.text}</p>
            <div className="flex items-center gap-3">
              <img src={r.avatar} alt={r.name} loading="lazy" className="w-11 h-11 rounded-full object-cover" />
              <div>
                <strong className="block text-ink">{r.name}</strong>
                <span className="text-[0.82rem] text-muted">Google review</span>
              </div>
            </div>
          </Reveal>
        ))}
        <div className="hidden lg:flex bg-amber rounded-3xl p-7 flex-col justify-center">
          <strong className="text-5xl font-extrabold text-ink leading-none">4.9★</strong>
          <p className="mt-3 font-bold text-ink">Across 100s of reviews from South African makers.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
        {STATS.map((s) => (
          <div key={s.label} className="text-center bg-white rounded-2xl border border-line py-7 px-4">
            <strong className="block text-3xl md:text-4xl font-extrabold text-ink tracking-tight">{s.value}</strong>
            <span className="block mt-2 font-semibold text-body text-sm">{s.label}</span>
          </div>
        ))}
      </div>
    </Section>

    {/* FEATURED SHOPS */}
    {shops.length > 0 && (
      <Section tone="white" pad="lg">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-14">
          <div className="max-w-[40ch]">
            <Eyebrow>Made on Shopstar</Eyebrow>
            <h2 className="mt-4 text-h2 font-extrabold text-ink">Stores we love, built by makers like you.</h2>
            <p className="mt-3 text-lead text-body">Real South African businesses, selling online right now.</p>
          </div>
          <Button href="/our-featured-shops" variant="secondary" icon={false}>Visit the showcase</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {shops.slice(0, 6).map((shop, i) => (
            <Reveal key={shop.slug || shop.url} delay={(i % 3) * 60}>
              <ShopCard shop={shop} />
            </Reveal>
          ))}
        </div>
      </Section>
    )}

    {/* MEET THE MAKERS */}
    <Section tone="cream" pad="lg">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-14">
        <div className="max-w-[40ch]">
          <Eyebrow>Stars of Shopstar</Eyebrow>
          <h2 className="mt-4 text-h2 font-extrabold text-ink">Meet the makers.</h2>
          <p className="mt-3 text-lead text-body">Real South African entrepreneurs, telling the story behind their stores.</p>
        </div>
        <Button href={PLAYLIST_URL} target="_blank" rel="noreferrer" variant="secondary" icon={false}>Watch on YouTube</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
        {MAKER_VIDEOS.map((video, i) => (
          <Reveal key={video.id} delay={(i % 3) * 60}>
            <VideoCard video={video} />
          </Reveal>
        ))}
      </div>
    </Section>

    <CtaBand />
  </Layout>
);

export default Home;
